<template>
  <div
    id="element"
    class="confirm-email"
  >
    <div class="confirm-email--item ">
      <Header/>
    </div>
    <div
      v-if="isAlreadyLogin"
      class="section-gray"
    >
      <vue-recaptcha
        ref="recaptchaConfirmEmail"
        :sitekey="siteKey"
        size="invisible"
        @verify="confirmEmail"
        @expired="onCaptchaConfirmEmailExpired"
      />
      <vue-element-loading
        v-if="confirmationStatus === 'VERIFYING'"
        :text="$t('confirm_email.verifying')"
        :text-style="{fontSize: '18px', color: '#2193ff', fontWeight: 'bold' }"
        spinner="bar-fade-scale"
        active
        color="#2193ff"
        background-color="rgba(255, 255, 255, 0.5)"
        size="100"
      />
      <div
        v-else-if="confirmationStatus === 'SUCCESS'"
        class="section-white"
      >
        <div class="confirm-email--title">
          <div style="margin-bottom: 32px;">
            <img
              src="/static/svg/icon-complete.svg"
              class="status-logo"
            >
            <span class="status-name">{{ $t('confirm_email.activated') }}</span>
          </div>
          {{ $t('confirm_email.welcome') }}
        </div>
        <div class="confirm-email--detail">
          {{ $t('confirm_email.activate_detail') }}
        </div>
        <div
          class="main-btn"
          @click="$router.push({ name: 'Exchange' })"
        >
          {{ $t('confirm_email.go_to_exchange') }}
        </div>
      </div>
      <div
        v-else
        class="section-white"
      >
        <vue-recaptcha
          ref="recaptchaSendEmail"
          :sitekey="siteKey"
          size="invisible"
          @verify="sendEmail"
          @expired="onCaptchaSendEmailExpired"
        />
        <div class="confirm-email--title">
          <div style="margin-bottom: 32px;">
            <img
              src="/static/svg/icon-fail.svg"
              class="status-logo"
            >
            <span class="status-name">{{ $t('confirm_email.error') }}</span>
          </div>
          {{ $t('confirm_email.error_title') }}
        </div>
        <div
          class="confirm-email--detail"
          v-html="$t('confirm_email.error_detail')"
        />
        <b-button
          :loading="isLoading"
          class="main-btn"
          @click="executeRecaptchaSendEmail()"
        >
          {{ $t('confirm_email.resend_email') }}
        </b-button>
        <div
          class="sub-btn"
          @click="$router.push({ name: 'Exchange' })"
        >
          {{ $t('confirm_email.go_to_exchange') }}
        </div>
      </div>
    </div>
    <div
      v-else
      class="section-gray"
    >
      <div class="section-white">
        <div class="confirm-email--title">
          {{ $t('confirm_email.login_first') }}
        </div>
        <div
          class="confirm-email--detail"
          v-html="$t('confirm_email.click_btn_below')"
        />
        <div
          class="button is-primary login-btn"
          @click="openLoginModal"
        >
          {{ $t('buttons.login') }}
        </div>
      </div>
    </div>
    <div class="confirm-email--item">
      <Footer/>
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { mapState } from 'vuex'
import Service from './service'
import { kulapApi } from '@/resources'
import Header from '@/features/Header'
import Footer from '@/components/Footer'
import CongratsToGetReward from '@/components/CongratsToGetReward'
import cookie from '@/utils/cookie'
import LoginModal from '@/features/LoginModal'
import { calTotalKULToken, showToastError } from '@/utils/helper'
import { RECAPTCHA_SITE_KEY } from '@/constants'

export default {
  name: 'ConfirmEmail',
  components: {
    Header,
    Footer,
    LoginModal,
    VueRecaptcha
  },
  data () {
    return {
      service: null,
      confirmationStatus: 'VERIFYING',
      isAlreadyLogin: false,
      isLoading: false,
      userDataFromCookie: {},
      errMsgUserNotMatch: 'Confirmation code is not match with this user.',
      siteKey: RECAPTCHA_SITE_KEY
    }
  },
  computed: {
    ...mapState({
      user: state => state.user
    })
  },
  async created () {
    this.service = new Service({
      store: this.$store,
      kulapApi
    })
    if (this.checkIsAlreadyLogin()) {
      this.isAlreadyLogin = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.executeRecaptchaConfirmEmail()
        }, 3000)
      })
    } else {
      this.openLoginModal()
    }
  },
  methods: {
    checkIsAlreadyLogin () {
      this.userDataFromCookie = cookie.get('user') ? JSON.parse(cookie.get('user')) : {}
      return this.userDataFromCookie.email && cookie.get('accessToken')
    },
    async confirmEmail (recaptchaToken) {
      const query = this.$route.query
      if (!this.checkIsAlreadyLogin()) {
        this.isAlreadyLogin = false
        return
      }
      if (query.code && query.email) {
        if (query.email !== this.userDataFromCookie.email) {
          this.service.logout()
          this.isAlreadyLogin = false
          this.$buefy.toast.open({
            duration: 5000,
            message: this.errMsgUserNotMatch,
            type: 'is-danger'
          })
          return
        }
        this.isAlreadyLogin = true
        try {
          await this.service.confirmEmail(query.code, query.email, recaptchaToken)
          await this.service.updateUserData()
          this.confirmationStatus = 'SUCCESS'
          this.congratsAfterRegister()
        } catch (e) {
          this.onCaptchaConfirmEmailExpired()
          this.confirmationStatus = 'ERROR'
          showToastError(e)
        }
        if (!window.Cypress) {
          this.$refs.recaptchaSendEmail.reset()
        }
      } else {
        this.onCaptchaConfirmEmailExpired()
        this.confirmationStatus = 'ERROR'
      }
    },
    executeRecaptchaSendEmail () {
      if (!window.Cypress) {
        this.isLoading = true
        this.$refs.recaptchaSendEmail.execute()
      } else {
        this.sendEmail()
      }
    },
    executeRecaptchaConfirmEmail () {
      if (!window.Cypress) {
        this.isLoading = true
        this.isAlreadyLogin = true
        this.$refs.recaptchaConfirmEmail.execute()
      } else {
        this.confirmEmail()
      }
    },
    async sendEmail (recaptchaToken) {
      try {
        await this.service.sendConfirmationEmail(recaptchaToken)
        this.$buefy.toast.open({
          message: 'It has been done, please check your email.',
          type: 'is-success'
        })
      } catch (e) {
        showToastError(e)
      }
      if (!window.Cypress) {
        this.$refs.recaptchaSendEmail.reset()
      }
      this.isLoading = false
    },
    openLoginModal () {
      this.$buefy.modal.open({
        parent: this,
        component: LoginModal,
        hasModalCard: true,
        canCancel: false,
        events: {
          loginSuccess: (data) => {
            this.executeRecaptchaConfirmEmail()
          }
        }
      })
    },
    async congratsAfterRegister () {
      if (this.user.rewards.length) {
        const filterRewards = this.user.rewards.filter(e => {
          return [ 'Registration', 'SubscribeNewsLetter', 'ConnectWithGoogle', 'ConnectWithFacebook' ].includes(e.name) && e.options.isOpenCongratsModal
        })
        if (filterRewards.length) {
          this.$buefy.modal.open({
            parent: this,
            component: CongratsToGetReward,
            hasModalCard: true,
            props: {
              tokenAmount: calTotalKULToken(filterRewards),
              subTitle: this.$t('messages.thanks_for_registration')
            },
            canCancel: ['escape', 'outside']
          })
          const changeRewardsOption = filterRewards.map(e => ({ ...e, options: { isOpenCongratsModal: false } }))
          await kulapApi.putRewardsOption(changeRewardsOption)
        }
      }
    },
    onCaptchaSendEmailExpired () {
      this.$refs.recaptchaSendEmail.reset()
      this.isLoading = false
    },
    onCaptchaConfirmEmailExpired () {
      this.$refs.recaptchaConfirmEmail.reset()
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.confirm-email {
  float: left;
  width: 100%;
  min-height: 100vh;

  &--item {
    float: left;
    width: 100%;
  }

  &--title {
    margin: 0 0 25px 0;
    font-size: 21px;
    font-weight: bold;
    text-align: center;
    color: #000000;
  }

  &--detail {
    color: grey;
    text-align: center;
  }
}
.section-gray {
  float: left;
  width: 100%;
  padding-bottom: 70px;
  text-align: center;
  min-height: calc(100vh - 263px - 142px);
}
.section-white {
  display: inline-block;
  padding: 32px 47px;
  width: fit-content;
  max-width: 400px;
  border-radius: 5px;
  box-shadow: 0 2px 4px -3px rgba(0, 0, 0, 0.5);
  background-color: #ffffff;
  text-align: left;
}
.verifying-box {
  min-width: 240px;
  text-align: center;
}
.status-logo {
  width: 37px;
  vertical-align:middle
}
.status-name {
  font-size: 27px;
  font-weight: bold;
  vertical-align:middle
}
.main-btn {
  margin-top: 30px;
  float: left;
  width: 100%;
  text-align: center;
  color: #fff;
  border: unset;
  background-color: #0458af;
  padding: 23px 15px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 3px;
  cursor: pointer;
}
.sub-btn {
  margin-top: 30px;
  float: left;
  width: 100%;
  text-align: center;
  color: #363636;
  background-color: #fff;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid #dbdbdb;
}
.sub-btn:hover {
  border: 1px solid #9c9c9c;
}
.login-btn {
  width: 100%;
  margin-top: 30px;
}
/* Less than tablet */
@media only screen and (max-width: 1087px) {
  .section-gray {
    padding-bottom: 1rem;
  }
}
/* Mobile */
@media only screen and (max-width: 768px) {
  .section-white {
    width: 95vw;
    max-width: calc(100vw - 20px);
    padding: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/ConfirmEmail/index.vue